import { useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { DISCOUNT_IDS } from '@constants/apiConstants';
import { gql } from '@generated/gql';
import { BundleQuoteFragment, ProductType } from '@generated/gql/graphql';
import {
  AboutYouFormValues,
  YourDetailsFormValues,
} from '@src/types/yourDetails';

const generateTelcoContract = gql(`
  mutation GenerateTelcoContract($input: GenerateEECCContractInput!) {
    generateEECCContract(input: $input) {
      link
    }
  }
`);

/**
 * @returns `true` if multiple services are enabled, `false` otherwise
 */
const useTelcoLegal = (
  selectedQuotes: BundleQuoteFragment[],
  values: YourDetailsFormValues | AboutYouFormValues
) => {
  const { enqueueSnackbar } = useSnackbar();

  const [generateContact] = useMutation(generateTelcoContract);

  const telcoProductTypes = [ProductType.Broadband, ProductType.Landline];
  const fixedLineQuote = selectedQuotes.find((q) =>
    telcoProductTypes.includes(q.product.type)
  );
  const mobileQuotes = selectedQuotes.filter(
    (q) => q.product.type === ProductType.Mobile
  );
  const hasTelcoService = !!fixedLineQuote || mobileQuotes.length > 0;

  const [link, setLink] = useState<string | Error | undefined>();

  const contractError = () => {
    enqueueSnackbar('Error generating contract, please try again', {
      variant: 'error',
      autoHideDuration: 5000,
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
    });
  };
  const contractLoading = () => {
    return enqueueSnackbar(
      'We are still generating your contract, please try again in a moment',
      {
        variant: 'info',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      }
    );
  };

  const generateContract = async () => {
    if (!hasTelcoService) return;
    try {
      const { data } = await generateContact({
        variables: {
          input: {
            email: values.aboutEmail,
            firstName: values.aboutNameFirst,
            lastName: values.aboutNameLast,
            title: values.aboutNameTitle,
            fixedLineProductDetails: fixedLineQuote
              ? {
                  quoteId: fixedLineQuote?.id,
                  tryBeforeYouBuy:
                    fixedLineQuote?.discounts?.some(
                      (discount) =>
                        discount.id ===
                          DISCOUNT_IDS.TBYB_BROADBAND_LINE_DISCOUNT &&
                        discount.qualified
                    ) ?? false,
                }
              : undefined,
            mobileProductDetails: mobileQuotes
              ? mobileQuotes.map((q) => ({ quoteId: q.id }))
              : undefined,
          },
        },
      });
      if (data) {
        setLink(data?.generateEECCContract.link);
      } else {
        setLink(new Error('Error generating contract'));
      }
    } catch (_error) {
      setLink(new Error('Error generating contract'));
    }
  };

  const downloadContract = async () => {
    if (!hasTelcoService) return;
    if (link instanceof Error) {
      contractError();
      await generateContract();
    } else if (link) {
      window.open(link, '_blank')?.focus();
    } else {
      contractLoading();
    }
  };

  return {
    hasTelcoService,
    generateContract,
    downloadContract,
  };
};

export { useTelcoLegal };
